// @flow
// $FlowFixMe
import React, {Component, Suspense, lazy} from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {FormattedMessage} from 'react-intl';
import classNames from 'classnames';
import {isToday, parseISO} from 'date-fns';
import {filter as filterGraphQlAnywhere} from 'graphql-anywhere';
import {Toaster} from '@homeaway/react-toaster';
import {Unveil} from '@homeaway/react-unveil';
import {
    MesoAds,
    MesoAdsContextWithSearchParams
} from '@homeaway/react-meso-ads';
import Experiment, {On, Off} from '@homeaway/react-experiment';
import {logExperiment} from '@homeaway/abacus-client-utils';
import AnalyticsDataLayer, {
    fragments as analyticsDataLayerFragments
} from '../AnalyticsDataLayer/AnalyticsDataLayer';
import FooterPlaceholder from '../AsyncFooter/FooterPlaceholder';
import HeaderBar from '../ConnectedHeader/ConnectedHeader';
import DropdownPanelOverlay from '../DropdownPanel/DropdownPanelOverlay';
import OverlayUpdateInterceptor from '../OverlayUpdateInterceptor/OverlayUpdateInterceptor';
import Sticky from '../Sticky/Sticky';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import SearchFormContainer from '../SearchFormContainer/SearchFormContainer';
import FlexibleDatesContainer from '../FlexibleDates/FlexibleDatesContainer';
import FiltersContainer from '../Filters/FiltersContainer';
import {MAP_VIEW} from '../../constants/views';
import Map from '../Map/Map';
import Results from '../Results/Results';
import ResultsAriaLiveStatus from '../ResultsAriaLiveStatus/ResultsAriaLiveStatus';
import NoResultsBannerWithDatepickerButton from '../NoResultsBannerWithDatepickerButton/NoResultsBannerWithDatepickerButton';
import MapButton from '../MapButton/MapButton';
import MapHitsDataProvider from '../MapHitsDataProvider/MapHitsDataProvider';
import SearchTermCarousel from '../SearchTermCarousel/SearchTermCarousel';
import DestinationCarousel from '../DestinationCarousel/DestinationCarousel';
import RecentlyViewedCarouselContainer from '../RecentlyViewedCarousel/RecentlyViewedCarouselContainer';
import {media} from '../../utils/breakpoints';
import routerActions from '../../actions/router';
import redirectActions from '../../actions/redirect';
import viewportActions from '../../actions/viewport';
import topBannersActions from '../../actions/topBanners';
import withWindowResize from '../withWindowResize';
import withTopBanners from '../withTopBanners/withTopBanners';
import FilteredCountDataProvider from '../FilteredCountDataProvider/FilteredCountDataProvider';
import withSearchRequestQuery from '../withSearchRequestQuery/withSearchRequestQuery';
import InternalTools from '../InternalTools/InternalTools';
import {isDateless} from '../../utils/searchParamsUtils';
import SeoLinkpile from '../SeoLinkpile/SeoLinkpile';
import {SearchBannerExperimentWrapper} from '../SearchBannerExperimentWrapper/SearchBannerExperimentWrapper';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import TripBoardsProviderContainer from '../TripBoardsProviderContainer/TripBoardsProviderContainer';
import {
    HA_PTA_LANDING_CLADSONSERP,
    VRBO_RECO_LARGE_SEARCH_DESTINO,
    MESO_VRBO_DISPLAY_ADS,
    VRBO_RECENTLY_VIEWED_SERP_CAROUSEL
} from '../../abacus/experiments';
import {PreFooterWithSeoData} from '../PreFooter/PreFooter';
import {HtmlTitleWithSeoData} from '../HtmlTitle/HtmlTitle';
import {logSearchTermCarousel} from '../../utils/carouselLogUtils';
import {safeDestructiveReadStorage} from '../../utils/localStorageWrapper';
import {
    getShowDestinationCarousel,
    getShowFlexibleDatesContainer
} from './utils/displayUtils';

// Lazy Components
/* istanbul ignore next */
const AsyncFooter = lazy(() =>
    import(/* webpackChunkName: "AsyncFooter" */ '../AsyncFooter/Footer')
);

import '../../_overrides.less';
import '../../_utilities.less';
import './Application.less';
import '../../_browser.less';

import type {CallbackProps as FilteredCountDataShape} from '../FilteredCountDataProvider/FilteredCountDataProvider';
import type {Environment} from '@homeaway/environment-resolver';
import type {SearchRequestQueryResponseData} from '../../queries/searchRequestQuery';
import type {State as AbacusState} from '../../reducers/abacus';
import type {State as ReduxState} from '../../reducers/index';
import type {SystemData} from '../../../server/utils/systemData';
import type {SearchParamsType} from '../../reducers/searchParams';

type StateProps = {
    abacus: AbacusState,
    allowBanners: boolean,
    environment: Environment,
    filterModalOpen: boolean,
    filter: ?(string[]),
    isBoundingBoxSearch?: boolean,
    pinnedPropertyListing?: string,
    isDatelessSearch: boolean,
    isFullScreenMapView: boolean,
    isInternalUser: boolean,
    isLoggedIn: boolean,
    isMapShowing: boolean,
    isRedirecting: boolean,
    isSubsequentFlexDatesView: boolean,
    isXsScreenWidth: boolean,
    fullLocale: string,
    scrollY: number,
    showBrandTransitionBanner: string,
    showOverlay: boolean,
    siteName: string,
    ssr?: boolean,
    system: SystemData,
    tripBoardProviderSearchParams: {
        adultsCount?: number,
        startDate?: string,
        childrenCount?: number,
        endDate?: string,
        petIncluded?: boolean,
        searchTerm?: string
    },
    topBannersHeight: number,
    mesoAdsContextSearchParams: {
        pathname: string,
        params: SearchParamsType,
        prevParams: SearchParamsType
    }
};

type WithSearchRequestQueryProps = {
    graphiqlUrl: string
};

type OwnProps = {
    data: SearchRequestQueryResponseData,
    onBackToList: typeof routerActions.list,
    onResize: typeof viewportActions.sizeChanged,
    onTopBannersSizeChanged: typeof topBannersActions.sizeChanged,
    redirect: typeof redirectActions.redirect
};

export type Props = StateProps & WithSearchRequestQueryProps & OwnProps;

const renderFilterFormWithFilteredCountData = ({
    resultsCount,
    filterGroups
}: FilteredCountDataShape) => (
    <FiltersContainer resultsCount={resultsCount} filterGroups={filterGroups} />
);

type State = {
    isMesoAdR1Loaded: boolean
};

const FIVE_MINUTES = 5 * 60 * 1000;

export class App extends Component<Props, State> {
    /* istanbul ignore next */
    constructor(props: Props) {
        super(props);
        this.state = {
            isMesoAdR1Loaded: false
        };
    }

    componentDidUpdate({isMapShowing: prevIsMapShowing}: Props) {
        const {
            data,
            isFullScreenMapView,
            isMapShowing,
            onBackToList,
            scrollY,
            filter
        } = this.props;

        // Restore the scroll position if we're coming back from map view or
        // filters modal
        if (
            typeof window !== 'undefined' &&
            prevIsMapShowing &&
            !isMapShowing
        ) {
            window.scrollTo(0, scrollY);
        }

        if (isFullScreenMapView && !data.loading && !data.results) {
            onBackToList();
        }

        if (data && data.results) {
            const {discoveryXploreFeeds, resultCount, geography} = data.results;
            const lbsId = geography ? geography.lbsId : '';

            logSearchTermCarousel(
                discoveryXploreFeeds,
                resultCount,
                lbsId,
                filter
            );
        }
    }

    onMesoAdR1Loaded = () => {
        this.setState({
            isMesoAdR1Loaded: true
        });
    };

    onMesoAdR1Failed = () => {
        this.setState({
            isMesoAdR1Loaded: false
        });
    };

    render() {
        const {
            abacus,
            allowBanners,
            data,
            filterModalOpen,
            graphiqlUrl,
            isBoundingBoxSearch,
            pinnedPropertyListing,
            isDatelessSearch,
            isFullScreenMapView,
            isInternalUser,
            isMapShowing,
            isRedirecting,
            isSubsequentFlexDatesView,
            isLoggedIn,
            isXsScreenWidth,
            onBackToList,
            redirect,
            showOverlay,
            siteName,
            system,
            topBannersHeight,
            showBrandTransitionBanner,
            tripBoardProviderSearchParams,
            environment,
            mesoAdsContextSearchParams
        } = this.props;

        const {loading, results, error, requestmarker} = data;

        const {
            destination,
            destinationRecommendationResponse,
            internalTools,
            listings,
            resultCount,
            pageCount,
            page,
            propertyRedirectUrl,
            destinationMessage,
            typeaheadSuggestion,
            discoveryXploreFeeds,
            topRecentReviews,
            geography,
            globalMessages,
            percentBooked
        } = results || {};

        if (!loading && propertyRedirectUrl) {
            redirect(propertyRedirectUrl);
        }

        const isResultsError = !loading && !results;
        const isRedirectingToList = isResultsError && isFullScreenMapView;
        const mapButtonDisabled = isXsScreenWidth && isResultsError;

        const classes = classNames('Application', siteName, {
            'Application--overlay': showOverlay,
            'Application--fullScreenMap': isFullScreenMapView
        });

        const applicationMapClasses = classNames('Application__map', {
            'Application__map--fullScreen': isFullScreenMapView
        });

        const applicationWithAdsClasses = classNames({
            'Application__with--R1ADS': this.state.isMesoAdR1Loaded,
            'Application__with--CLADS':
                (abacus[HA_PTA_LANDING_CLADSONSERP] || {}).value > 0
        });

        const topBannerMargin = {marginTop: topBannersHeight};
        const applicationMapStyles = allowBanners ? topBannerMargin : {};
        const isLoadingOrRedirecting =
            loading || isRedirecting || isRedirectingToList;

        const showBackToList = isFullScreenMapView;

        const hasResults = !!(listings && listings.length);

        const showNoResultsBannerWithDatepickerButton = !!(
            page === 1 &&
            tripBoardProviderSearchParams.startDate &&
            isToday(parseISO(tripBoardProviderSearchParams.startDate))
        );

        const filterbarClasses = classNames('Application__filterBar');

        const adlFragment =
            (!isLoadingOrRedirecting &&
                !!results &&
                filterGraphQlAnywhere(
                    analyticsDataLayerFragments.SearchResult,
                    results
                )) ||
            null;

        const isAdsFeatureGateEnabled =
            (abacus[MESO_VRBO_DISPLAY_ADS] || {}).value > 0;
        const isClAdsEnabled =
            (abacus[HA_PTA_LANDING_CLADSONSERP] || {}).value > 0;
        const isAdsEnabled =
            isAdsFeatureGateEnabled && (system.isDesktop || isClAdsEnabled);

        const showDestinationRecommendations =
            safeDestructiveReadStorage(
                'homepage:suppressCarousel',
                FIVE_MINUTES
            ) !== 'hide';

        // display conditions for destinations and flexible dates carousels were moved to one place
        // recently viewed carousel rendering will depend on combination of these conditions

        // display conditions source for flexible dates carousel:
        // https://github.expedia.biz/vrbo-shopping-experience/node-traveler-search-ui/blob/f6c0327c49cdb3bd468c4cada85faae052650b34/src/universal/components/FlexibleDates/FlexibleDatesContainer.js#L66
        const showFlexibleDatesContainer = getShowFlexibleDatesContainer(
            isDatelessSearch,
            isSubsequentFlexDatesView,
            resultCount,
            percentBooked
        );
        // display conditions source for destinations carousel:
        // https://github.expedia.biz/vrbo-shopping-experience/node-traveler-search-ui/blob/f6c0327c49cdb3bd468c4cada85faae052650b34/src/universal/components/Application/Application.js#L419
        const showDestinationCarousel = getShowDestinationCarousel(
            isLoadingOrRedirecting,
            typeaheadSuggestion,
            pageCount,
            resultCount,
            discoveryXploreFeeds,
            showDestinationRecommendations
        );

        const isRecentlyViewedCarouselV1 =
            (abacus[VRBO_RECENTLY_VIEWED_SERP_CAROUSEL] || {}).value === 1;

        const showRecentlyViewedCarousel =
            isRecentlyViewedCarouselV1 &&
            !pinnedPropertyListing &&
            !showFlexibleDatesContainer &&
            !showDestinationCarousel;

        if (!showRecentlyViewedCarousel) {
            logExperiment(VRBO_RECENTLY_VIEWED_SERP_CAROUSEL);
        }

        return (
            <div className={classes} style={topBannerMargin}>
                <AnalyticsDataLayer
                    results={adlFragment}
                    requestmarker={requestmarker}
                />
                <header
                    className="Application__header"
                    data-wdio="HeaderBarContainer"
                >
                    <HtmlTitleWithSeoData
                        results={results}
                        resultsLoading={loading}
                        page={page}
                        pageCount={pageCount}
                    />

                    <HeaderBar
                        internalTools={
                            isInternalUser ? (
                                <InternalTools
                                    searchServiceUrl={
                                        internalTools &&
                                        internalTools.searchServiceUrl
                                    }
                                    graphiqlUrl={graphiqlUrl}
                                    environment={environment}
                                />
                            ) : null
                        }
                        showBrandTransitionBanner={showBrandTransitionBanner}
                    />
                    <SearchFormContainer />
                </header>
                {isXsScreenWidth && (
                    <Sticky
                        className="Application__stickyFilterBarContainer"
                        top={0}
                    >
                        {() => (
                            <section className="Application__filterBar">
                                <FiltersContainer
                                    displayMoreFiltersPillLeft
                                    displayNumGroupsAsPills={3}
                                    searchResultsLoading={
                                        isLoadingOrRedirecting
                                    }
                                    showAllFiltersPill
                                    showMoreFiltersPill={false}
                                />
                                <MapButton
                                    mapButtonDisabled={mapButtonDisabled}
                                />
                            </section>
                        )}
                    </Sticky>
                )}
                <MesoAdsContextWithSearchParams
                    brandId={system.brandId}
                    gaiaId={geography && geography.gaiaId}
                    havId={system.havId}
                    locale={system.locale}
                    searchParams={mesoAdsContextSearchParams}
                    isAdsEnabled={isAdsEnabled}
                >
                    <div className="Application__container">
                        {!isXsScreenWidth && (
                            <section className={filterbarClasses}>
                                <FiltersContainer
                                    displayMoreFiltersPillLeft
                                    displayNumGroupsAsPills={3}
                                    searchResultsLoading={
                                        isLoadingOrRedirecting
                                    }
                                    showAllFiltersPill
                                    showMoreFiltersPill={false}
                                />
                            </section>
                        )}
                        <div
                            id="Application__resultsViewport"
                            className="Application__resultsViewport"
                        >
                            <OverlayUpdateInterceptor>
                                <div
                                    className={classNames(
                                        'Application__resultsColumn',
                                        applicationWithAdsClasses
                                    )}
                                >
                                    <section className="Application__results Application__section--padded">
                                        <h1 className="sr-only">
                                            <FormattedMessage
                                                id="appSearchResultsSection"
                                                defaultMessage="Search results"
                                            />
                                        </h1>
                                        {showRecentlyViewedCarousel && (
                                            <section className="Application__recentlyViewedCarousel">
                                                <RecentlyViewedCarouselContainer
                                                    isLoading={
                                                        isLoadingOrRedirecting
                                                    }
                                                />
                                            </section>
                                        )}
                                        <ErrorBoundary>
                                            {showDestinationCarousel && (
                                                <Experiment
                                                    name={
                                                        VRBO_RECO_LARGE_SEARCH_DESTINO
                                                    }
                                                >
                                                    <On>
                                                        {!isBoundingBoxSearch &&
                                                            destinationRecommendationResponse && (
                                                                <section className="Application__destinationCarousel">
                                                                    <DestinationCarousel
                                                                        isLoading={
                                                                            loading
                                                                        }
                                                                        carouselData={
                                                                            destinationRecommendationResponse
                                                                        }
                                                                        typeaheadSuggestion={
                                                                            typeaheadSuggestion
                                                                        }
                                                                    />
                                                                </section>
                                                            )}
                                                    </On>
                                                    <Off>
                                                        <section className="Application__searchTermCarousel">
                                                            <SearchTermCarousel
                                                                discoveryXploreFeeds={
                                                                    discoveryXploreFeeds
                                                                }
                                                                typeaheadSuggestion={
                                                                    typeaheadSuggestion
                                                                }
                                                            />
                                                        </section>
                                                    </Off>
                                                </Experiment>
                                            )}
                                            {!isLoadingOrRedirecting &&
                                                (destinationMessage ||
                                                    globalMessages) && (
                                                    <SearchBannerExperimentWrapper
                                                        destinationMessage={
                                                            destinationMessage
                                                        }
                                                        globalMessages={
                                                            globalMessages
                                                        }
                                                        hasResults={hasResults}
                                                    />
                                                )}

                                            {showNoResultsBannerWithDatepickerButton && (
                                                <NoResultsBannerWithDatepickerButton />
                                            )}

                                            <ResultsAriaLiveStatus
                                                isLoading={
                                                    isLoadingOrRedirecting
                                                }
                                                pageNumber={
                                                    results && results.page
                                                }
                                            />

                                            {showFlexibleDatesContainer && (
                                                <FlexibleDatesContainer
                                                    loading={
                                                        isLoadingOrRedirecting
                                                    }
                                                    resultCount={resultCount}
                                                    percentBooked={
                                                        percentBooked
                                                    }
                                                />
                                            )}

                                            <Results
                                                error={error}
                                                results={results}
                                                loading={isLoadingOrRedirecting}
                                                allowUpdate={
                                                    !isFullScreenMapView
                                                }
                                            />
                                        </ErrorBoundary>
                                    </section>
                                    <section id="Application__preFooter">
                                        <PreFooterWithSeoData
                                            results={results}
                                            resultsLoading={loading}
                                            topRecentReviews={topRecentReviews}
                                        />
                                    </section>
                                    <section
                                        className="Application__footer"
                                        role="contentinfo"
                                        data-wdio="FooterBar"
                                    >
                                        <Breadcrumb destination={destination} />
                                        <ErrorBoundary errorName="seoLinkpile">
                                            <SeoLinkpile
                                                results={results}
                                                resultsLoading={loading}
                                            />
                                        </ErrorBoundary>
                                        <ErrorBoundary errorName="AsyncFooter">
                                            <Unveil threshold={400}>
                                                <Suspense
                                                    fallback={FooterPlaceholder}
                                                >
                                                    <AsyncFooter />
                                                </Suspense>
                                            </Unveil>
                                        </ErrorBoundary>
                                    </section>
                                </div>
                                <MesoAds
                                    slot={'R1'}
                                    onAdLoadSuccess={this.onMesoAdR1Loaded}
                                    onAdLoadFailed={this.onMesoAdR1Failed}
                                    style={applicationMapStyles}
                                />
                                <section
                                    className={applicationMapClasses}
                                    style={applicationMapStyles}
                                >
                                    <MapHitsDataProvider
                                        {...{
                                            loading: isLoadingOrRedirecting,
                                            onBackClick:
                                                showBackToList && onBackToList,
                                            options: {
                                                zoomControl:
                                                    isMapShowing &&
                                                    !showBackToList /* zoom conflicts with back to list */
                                            },
                                            render: (p) => <Map {...p} />,
                                            results,
                                            shouldShowFiltersButton: isXsScreenWidth,
                                            showBackToList,
                                            siteName,
                                            allowUpdate: isMapShowing
                                        }}
                                    />
                                </section>
                            </OverlayUpdateInterceptor>
                            <DropdownPanelOverlay />
                        </div>
                    </div>
                </MesoAdsContextWithSearchParams>

                {isXsScreenWidth && filterModalOpen && (
                    <section className="Application__filterForm">
                        <FilteredCountDataProvider
                            searchResultsLoading={isLoadingOrRedirecting}
                            children={renderFilterFormWithFilteredCountData}
                        />
                    </section>
                )}
                <OverlayUpdateInterceptor>
                    <Toaster />
                    <TripBoardsProviderContainer
                        siteName={siteName}
                        tripBoardProviderSearchParams={
                            tripBoardProviderSearchParams
                        }
                        isLoggedIn={isLoggedIn}
                    />
                </OverlayUpdateInterceptor>
            </div>
        );
    }
}

export const mapStateToProps = (state: ReduxState): StateProps => {
    let {
        modals,
        searchParams,
        system,
        viewport: {breakpoint, scrollY},
        topBanners: {height: topBannersHeight},
        abacus,
        redirect: {isRedirecting},
        overlay: {showOverlay},
        flexDates: {isSubsequentFlexDatesView},
        user: {isLoggedIn}
    } = state;

    const isXsScreenWidth = !media(breakpoint, {
        minWidth: 'SM_MIN'
    });

    const isSmScreenWidth =
        !media(breakpoint, {
            minWidth: 'MD_MIN'
        }) && !isXsScreenWidth;

    const isMdScreenWidth = media(breakpoint, {
        minWidth: 'MD_MIN'
    });

    const {isBoundingBoxSearch} = searchParams;

    const {
        adultsCount,
        arrival: startDate,
        childrenCount,
        departure: endDate,
        filter,
        pinnedPropertyListing,
        petIncluded,
        q: searchTerm,
        ssr,
        view
    } = searchParams.params;

    const isFullScreenMapView =
        view === MAP_VIEW && !media(breakpoint, {minWidth: 'MD_MIN'});

    const isMapShowing =
        isFullScreenMapView || media(breakpoint, {minWidth: 'MD_MIN'});

    const allowBanners =
        (isFullScreenMapView && !isSmScreenWidth) ||
        (system.isDesktop && isMdScreenWidth);

    const isInternalUser = system.isInternalUser;

    return {
        abacus,
        environment: system.environment,
        allowBanners,
        isBoundingBoxSearch,
        pinnedPropertyListing,
        isDatelessSearch: isDateless(searchParams.params),
        isFullScreenMapView,
        isInternalUser,
        isLoggedIn,
        isMapShowing,
        isRedirecting,
        isSubsequentFlexDatesView,
        isXsScreenWidth,
        filter,
        filterModalOpen: modals.FILTERS.isOpen,
        fullLocale: system.fullLocale,
        topBannersHeight,
        scrollY,
        showOverlay,
        siteName: system.siteName,
        ssr: !!ssr,
        tripBoardProviderSearchParams: {
            startDate,
            endDate,
            adultsCount,
            petIncluded,
            searchTerm,
            childrenCount
        },
        showBrandTransitionBanner: system.transitionBanner,
        system,
        mesoAdsContextSearchParams: {
            pathname: searchParams.pathname,
            params: searchParams.params,
            prevParams: searchParams.prevParams
        }
    };
};

const mapDispatchToProps = {
    onBackToList: routerActions.list,
    onResize: viewportActions.sizeChanged,
    onTopBannersSizeChanged: topBannersActions.sizeChanged,
    redirect: redirectActions.redirect
};

export const applicationCompose = (ui: any) =>
    compose(
        // Flow wants a type parameter passed for withSearchRequestQuery's
        // OwnProps, but I don't know how to annotate that when passing
        // withSearchRequestQuery as part of a compose pipeline.
        //
        // withSearchRequestQuery must come first or else its `shouldComponentUpdate`
        // will skip renders when Redux props change.
        // $FlowFixMe
        withSearchRequestQuery,
        connect(mapStateToProps, mapDispatchToProps),
        withWindowResize,
        withTopBanners
    )(ui);

const ApplicationComponent = applicationCompose(App);
const SERP_APPLICATION_CRASHED = 'SERP application crashed';

export default function Application() {
    return (
        <ErrorBoundary errorName={SERP_APPLICATION_CRASHED}>
            <ApplicationComponent />
        </ErrorBoundary>
    );
}
